import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { unwrap, unwrapVoid } from '../api-error';
import { useOptionalViewer } from '../viewer-context';
import { applyOptimisticPollVote } from './poll-vote-optimistic';
import {
  invalidateAndRefetchAll,
  restoreSnapshots,
  type SnapshotSet,
} from '../query-cache-utils';
import { transformAllListCaches } from '../cache/transform-all-list-caches';
import {
  feedKeys,
  isSavedScopeFeedListKey,
  reportImpressions,
  type AuthoredPostsPage,
} from '../queries/feed';
import { leaderActionsKeys } from '../queries/leader-actions';
import {
  bodyHasUrl,
  postContentSection,
  trackPollVoted,
  trackPostCommented,
  trackPostCreated,
  trackPostPinned,
  trackPostReacted,
  trackPostSaved,
  type ContentSection,
  type GroupKind,
} from '../analytics/events';
import { completeOnboardingActionIfPending } from '../../components/onboarding/progress';
import {
  useResolveLinkPreview,
  type ResolveLinkPreviewResult,
  type LinkPreviewInput,
} from './link-previews';

export { useResolveLinkPreview, type ResolveLinkPreviewResult, type LinkPreviewInput };

// openapi-typescript narrows `CreatePostRequest.linkPreviews`'s element to
// `LinkPreviewInput & Record<string, never>` (a codegen artifact for the
// request schema — the same quirk handled in useCreateGroupBroadcast). Expose
// the clean `LinkPreviewInput[]` to callers and cast back at the POST boundary.
export type CreatePostInput = Omit<
  components['schemas']['CreatePostRequest'],
  'linkPreviews'
> & { linkPreviews?: LinkPreviewInput[] };
// `section` is analytics-only (the post's content_section, computed by the
// component that renders the post) — it never reaches the API body.
// `linkPreviews` gets the same codegen-artifact treatment as CreatePostInput.
export type CreateCommentInput = Omit<
  components['schemas']['CreateCommentRequest'],
  'linkPreviews'
> & {
  postId: string;
  section: ContentSection;
  linkPreviews?: LinkPreviewInput[];
};
export type ReactionKind = components['schemas']['ReactToPostRequest']['kind'];
export type PresignPostMediaInput =
  components['schemas']['PresignPostMediaRequest'];
export type PresignPostMediaResult =
  components['schemas']['PresignPostMediaResponse'];
export type FinalizePostMediaInput =
  components['schemas']['FinalizePostMediaRequest'];
export type FinalizePostMediaResult =
  components['schemas']['FinalizePostMediaResponse'];
export type AttachedEntityInput = components['schemas']['AttachedEntityInput'];
export type ResolveUrlResult = components['schemas']['ResolveUrlResponse'];
export type ResolveAttachmentResult =
  components['schemas']['ResolveAttachmentResponse'];
type FeedItemPage = components['schemas']['PostsPage'];
type FeedItemView = components['schemas']['FeedItemView'];
type AuthoredPostView = components['schemas']['AuthoredPostView'];
type CommentPage = components['schemas']['CommentPage'];
type CommentShape = components['schemas']['CommentView'];

// Feed items are a tagged union — only `kind: 'post'` variants carry a post id —
// so the generic patchers in `lib/query-cache-utils.ts` (which assume top-level
// items have `.id`) don't fit cleanly. These feed-specific helpers walk every
// cached list (both useQuery `FeedItemPage` and useInfiniteQuery
// `InfiniteData<FeedItemPage>`) and apply the patch to matching post items.

type PostShape = Extract<FeedItemView, { kind: 'post' }>['post'];
// The non-null poll shape carried on a cached post (PostView.poll is
// `PollView | null`). Used as the authoritative return of usePollVote.
type PollShape = NonNullable<PostShape['poll']>;
type FeedListCache = FeedItemPage | InfiniteData<FeedItemPage>;
type AuthorPostsCache =
  | readonly AuthoredPostView[]
  | InfiniteData<AuthoredPostsPage>;

function mapPagePosts(
  page: FeedItemPage,
  fn: (post: PostShape) => PostShape | null,
): FeedItemPage {
  const items: FeedItemView[] = [];
  for (const item of page.items) {
    if (item.kind !== 'post') {
      items.push(item);
      continue;
    }
    const next = fn(item.post);
    if (next === null) continue; // remove
    items.push({ ...item, post: next });
  }
  return { ...page, items };
}

function transformAllFeedLists(
  qc: QueryClient,
  fn: (post: PostShape) => PostShape | null,
): SnapshotSet {
  return transformAllListCaches<FeedListCache>(
    qc,
    feedKeys.lists(),
    (value) => {
      if ('pages' in value) {
        return {
          ...value,
          pages: value.pages.map((p) => mapPagePosts(p, fn)),
        };
      }
      return mapPagePosts(value, fn);
    },
  );
}

function mapAuthorPostItems(
  items: readonly AuthoredPostView[],
  fn: (post: PostShape) => PostShape | null,
): AuthoredPostView[] {
  const next: AuthoredPostView[] = [];
  for (const item of items) {
    const patched = fn(item.post);
    if (patched === null) continue; // remove
    next.push({ ...item, post: patched });
  }
  return next;
}

// Author-posts caches (profile activity) participate in the same optimistic-
// patch flow as feed lists because PostCard seeds its local reaction state
// from the cached post on every mount — without this, navigating away from
// the profile and back loses the user's reaction (and the count) until the
// server roundtrip lands.
//
// `feedKeys.authorPostsAll()` is a prefix matching BOTH cache shapes the
// profile routes create: the flat useQuery cache (AuthoredPostView[], from
// feedKeys.authorPosts) and the useInfiniteQuery cache
// (InfiniteData<AuthoredPostsPage>, from feedKeys.authorPostsInfinite). Both
// must be handled — iterating the InfiniteData object as an array is what
// produced the "not iterable" crash when reacting after a member profile
// loaded. Mirrors transformAllFeedLists.
function transformAllAuthorPostsLists(
  qc: QueryClient,
  fn: (post: PostShape) => PostShape | null,
): SnapshotSet {
  return transformAllListCaches<AuthorPostsCache>(
    qc,
    feedKeys.authorPostsAll(),
    (value) => {
      if ('pages' in value) {
        return {
          ...value,
          pages: value.pages.map((p) => ({
            ...p,
            items: mapAuthorPostItems(p.items, fn),
          })),
        };
      }
      return mapAuthorPostItems(value, fn);
    },
  );
}

// The post-detail page reads its own `feedKeys.detail` entry (a bare
// `PostView | null`), so a patch that only walks the lists leaves that page
// without optimistic feedback.
function patchPostDetail(
  qc: QueryClient,
  postId: string,
  patch: (post: PostShape) => PostShape,
): SnapshotSet {
  const key = feedKeys.detail(postId).queryKey;
  const current = qc.getQueryData(key);
  if (current == null) return { previousLists: [] };
  qc.setQueryData(key, patch(current));
  return { previousLists: [[key, current]] };
}

// Patch one post everywhere it is cached: every feed list, every author-posts
// list, and its detail entry. Exported for unit tests in feed.test.ts. The
// mutation hooks below are the only production callers — there is no other
// importer in the codebase.
export function patchPostInCaches(
  qc: QueryClient,
  postId: string,
  patch: (post: PostShape) => PostShape,
): SnapshotSet {
  const matchOrSkip = (post: PostShape) =>
    post.id === postId ? patch(post) : post;
  return mergeSnapshots(
    transformAllFeedLists(qc, matchOrSkip),
    transformAllAuthorPostsLists(qc, matchOrSkip),
    patchPostDetail(qc, postId, patch),
  );
}

// Prepend a synthesised post item to the first page of every cached saved-
// scope feed list, copying the FeedItemView wrapper from the first cached list
// that already contains this post (so we get its `author` + `group` shape).
// If we don't have the post in any cache, we cannot insert (no shape).
function prependToSavedScopeLists(
  qc: QueryClient,
  postId: string,
): SnapshotSet {
  const entries = qc.getQueriesData<FeedListCache>({
    queryKey: feedKeys.lists(),
  });
  let template: Extract<FeedItemView, { kind: 'post' }> | undefined;
  for (const [, value] of entries) {
    if (value === undefined) continue;
    const pages = 'pages' in value ? value.pages : [value];
    for (const page of pages) {
      const hit = page.items.find(
        (i): i is Extract<FeedItemView, { kind: 'post' }> =>
          i.kind === 'post' && i.post.id === postId,
      );
      if (hit !== undefined) {
        template = hit;
        break;
      }
    }
    if (template !== undefined) break;
  }
  const previousLists: Array<readonly [readonly unknown[], unknown]> = [];
  if (template === undefined) return { previousLists };
  for (const [key, value] of entries) {
    if (value === undefined || !isSavedScopeFeedListKey(key)) continue;
    const head = 'pages' in value ? value.pages[0] : value;
    if (head === undefined) continue;
    // Don't double-insert if already present
    if (
      head.items.some(
        (i) => i.kind === 'post' && i.post.id === postId,
      )
    ) {
      continue;
    }
    previousLists.push([key, value]);
    const nextHead: FeedItemPage = {
      ...head,
      items: [template, ...head.items],
    };
    if ('pages' in value) {
      qc.setQueryData<InfiniteData<FeedItemPage>>(key, {
        ...value,
        pages: [nextHead, ...value.pages.slice(1)],
      });
    } else {
      qc.setQueryData<FeedItemPage>(key, nextHead);
    }
  }
  return { previousLists };
}

function removeFromSavedScopeLists(
  qc: QueryClient,
  postId: string,
): SnapshotSet {
  const drop = (post: PostShape): PostShape | null =>
    post.id === postId ? null : post;
  return transformAllListCaches<FeedListCache>(
    qc,
    feedKeys.lists(),
    (value) => {
      if ('pages' in value) {
        return {
          ...value,
          pages: value.pages.map((p) => mapPagePosts(p, drop)),
        };
      }
      return mapPagePosts(value, drop);
    },
    isSavedScopeFeedListKey,
  );
}

function mergeSnapshots(...sets: SnapshotSet[]): SnapshotSet {
  return {
    previousLists: sets.flatMap((s) => s.previousLists),
  };
}

// Patch one comment wherever it is cached: the post's top-level comment list
// and every loaded reply thread (a comment id appears in exactly one of the
// two, but the mutation doesn't know which — replies aren't distinguishable
// from parents by id alone).
function patchCommentInCaches(
  qc: QueryClient,
  postId: string,
  commentId: string,
  patch: (comment: CommentShape) => CommentShape,
): SnapshotSet {
  const mapPage = (page: CommentPage): CommentPage => {
    let changed = false;
    const items = page.items.map((c) => {
      if (c.id !== commentId) return c;
      changed = true;
      return patch(c);
    });
    return changed ? { ...page, items } : page;
  };
  const transform = (
    value: CommentPage | InfiniteData<CommentPage>,
  ): CommentPage | InfiniteData<CommentPage> | null => {
    if ('pages' in value) {
      const pages = value.pages.map(mapPage);
      return pages.some((p, i) => p !== value.pages[i])
        ? { ...value, pages }
        : null;
    }
    const next = mapPage(value);
    return next === value ? null : next;
  };
  return mergeSnapshots(
    transformAllListCaches<CommentPage | InfiniteData<CommentPage>>(
      qc,
      feedKeys.comments(postId).queryKey,
      transform,
    ),
    transformAllListCaches<CommentPage | InfiniteData<CommentPage>>(
      qc,
      feedKeys.commentRepliesAll(),
      transform,
    ),
  );
}

export function useCreateFeedPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePostInput) => {
      const client = getApiClient();
      const data = unwrap(await client.POST('/api/v1/posts', {
        body: {
          body: input.body,
          bodyFormat: input.bodyFormat,
          scope: input.scope,
          attachedEntities:
            input.attachedEntities !== undefined
              ? [...input.attachedEntities]
              : undefined,
          media: input.media !== undefined ? [...input.media] : undefined,
          // Cast back to the codegen's quirky element type (see CreatePostInput
          // above). Submitted verbatim — the server re-validates before write.
          linkPreviews:
            input.linkPreviews !== undefined
              ? ([...input.linkPreviews] as NonNullable<
                  components['schemas']['CreatePostRequest']['linkPreviews']
                >)
              : undefined,
          // Unlike linkPreviews, codegen does NOT narrow `poll` with a
          // `Record<string, never>` artifact — it's a clean `PollInput`, so it
          // flows in via the CreatePostInput Omit spread with no cast needed.
          poll: input.poll ?? undefined,
          clientPostId: input.clientPostId ?? undefined,
        },
      }));
      return data.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: feedKeys.lists() });
      qc.invalidateQueries({ queryKey: feedKeys.scopeCounts().queryKey });
      trackPostCreated({
        content_section: postContentSection(data),
        has_media: data.media.length > 0,
        has_link: bodyHasUrl(data.body),
        has_poll: data.poll !== null,
        mention_count: data.mentions.length,
      });
      // Reflect the onboarding "Create a post" (`first_post`) step immediately.
      // It's a derived step recorded by an async worker, so without this the
      // checklist card wouldn't advance until a manual reload. No-ops unless
      // the viewer is onboarding with `first_post` still pending.
      void completeOnboardingActionIfPending(qc, 'first_post');
      // A leader's own post can clear the board's quiet-week and
      // share-event tasks server-side; refetch rather than show stale rows.
      void qc.invalidateQueries({ queryKey: leaderActionsKeys.me().queryKey });
    },
  });
}

export function usePinPostInScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      postId: string;
      scopeGroupId: string;
      // Analytics-only: the pinned scope's group kind, from the group page
      // hosting the pin action.
      scopeGroupKind: GroupKind;
    }) => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/posts/{postId}/scopes/{groupId}/pin',
        {
          params: {
            path: { postId: input.postId, groupId: input.scopeGroupId },
          },
        },
      ));
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: feedKeys.lists() });
      qc.invalidateQueries({ queryKey: feedKeys.detail(vars.postId).queryKey });
      trackPostPinned({ content_section: vars.scopeGroupKind });
    },
  });
}

export function useUnpinPostInScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { postId: string; scopeGroupId: string }) => {
      const client = getApiClient();
      unwrapVoid(await client.DELETE(
        '/api/v1/posts/{postId}/scopes/{groupId}/pin',
        {
          params: {
            path: { postId: input.postId, groupId: input.scopeGroupId },
          },
        },
      ));
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: feedKeys.lists() });
      qc.invalidateQueries({ queryKey: feedKeys.detail(vars.postId).queryKey });
    },
  });
}

// Platform-wide featured slot (admin-only). Same invalidation shape as the
// pin mutations above: the broad `feedKeys.lists()` prefix (rank placement
// changes on every feed list) plus the post's own detail leaf.
export function useFeaturePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { postId: string }) => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/admin/posts/{id}/feature',
        { params: { path: { id: input.postId } } },
      ));
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: feedKeys.lists() });
      qc.invalidateQueries({ queryKey: feedKeys.detail(vars.postId).queryKey });
    },
  });
}

export function useUnfeaturePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { postId: string }) => {
      const client = getApiClient();
      unwrapVoid(await client.DELETE(
        '/api/v1/admin/posts/{id}/feature',
        { params: { path: { id: input.postId } } },
      ));
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: feedKeys.lists() });
      qc.invalidateQueries({ queryKey: feedKeys.detail(vars.postId).queryKey });
    },
  });
}

export type UpdateFeedPostMediaItem = NonNullable<
  components['schemas']['UpdatePostRequest']['media']
>[number];

export function useUpdateFeedPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      postId: string;
      body: string;
      bodyFormat: 'v1' | 'v2';
      // Complete desired media list in display order; omitted = unchanged.
      media?: ReadonlyArray<UpdateFeedPostMediaItem>;
    }) => {
      const client = getApiClient();
      const data = unwrap(await client.PATCH(
        '/api/v1/posts/{id}',
        {
          params: { path: { id: input.postId } },
          body: {
            body: input.body,
            bodyFormat: input.bodyFormat,
            ...(input.media !== undefined ? { media: [...input.media] } : {}),
          },
        },
      ));
      return data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: feedKeys.detail(vars.postId).queryKey });
      qc.invalidateQueries({ queryKey: feedKeys.lists() });
    },
  });
}

export function useDeleteFeedPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: string) => {
      const client = getApiClient();
      unwrapVoid(await client.DELETE('/api/v1/posts/{id}', {
        params: { path: { id: postId } },
      }));
      return postId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.lists() });
      qc.invalidateQueries({ queryKey: feedKeys.scopeCounts().queryKey });
    },
  });
}

export function useReactToFeedPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      postId: string;
      kind: ReactionKind;
      // Analytics-only: the post's content_section, from the rendering card.
      section: ContentSection;
    }) => {
      const client = getApiClient();
      const data = unwrap(await client.POST(
        '/api/v1/posts/{id}/reactions',
        {
          params: { path: { id: input.postId } },
          body: { kind: input.kind },
        },
      ));
      return data.data;
    },
    onMutate: async ({ postId, kind }) => {
      await qc.cancelQueries({ queryKey: feedKeys.lists() });
      await qc.cancelQueries({ queryKey: feedKeys.detail(postId).queryKey });
      const snapshot = patchPostInCaches(qc, postId, (post) => {
        const currentKind = post.viewerReaction ?? null;
        const removing = currentKind === kind;
        const next = { ...post.reactionCounts };
        if (currentKind !== null && currentKind in next) {
          next[currentKind as keyof typeof next] = Math.max(
            0,
            (next[currentKind as keyof typeof next] ?? 0) - 1,
          );
        }
        if (!removing) {
          next[kind as keyof typeof next] =
            (next[kind as keyof typeof next] ?? 0) + 1;
        }
        return {
          ...post,
          reactionCounts: next,
          viewerReaction: removing ? null : kind,
        };
      });
      return snapshot;
    },
    onError: (_err, _vars, ctx) => {
      restoreSnapshots(qc, ctx);
    },
    // A toggle: only the add direction is a tracked engagement.
    onSuccess: (data, vars) => {
      if (data.action === 'added') {
        trackPostReacted({
          reaction_type: vars.kind,
          content_section: vars.section,
        });
      }
    },
    onSettled: (_data, _err, { postId }) => {
      qc.invalidateQueries({ queryKey: feedKeys.detail(postId).queryKey });
    },
  });
}

export function usePollVote() {
  const qc = useQueryClient();
  const viewerId = useOptionalViewer()?.id ?? null;
  return useMutation({
    mutationFn: async (input: {
      postId: string;
      optionId: string;
      // Analytics-only: the post's content_section, from the rendering card.
      section: ContentSection;
    }): Promise<PollShape> => {
      const client = getApiClient();
      const data = unwrap(await client.POST('/api/v1/posts/{id}/poll/vote', {
        params: { path: { id: input.postId } },
        body: { optionId: input.optionId },
      }));
      // The server always returns a non-null poll (results revealed). Codegen
      // types `data.data` as `PollView | null` intersected with
      // `Record<string, never>` (a @hono/zod-openapi nullable-component +
      // response codegen artifact). Cast back to the clean shape and guard the
      // never-hit null so callers receive a non-null PollView.
      const poll = data.data as PollShape | null;
      if (poll === null) {
        throw new Error('poll_vote_missing_poll: vote response carried no poll');
      }
      return poll;
    },
    onMutate: async ({ postId, optionId }) => {
      await qc.cancelQueries({ queryKey: feedKeys.lists() });
      await qc.cancelQueries({ queryKey: feedKeys.detail(postId).queryKey });
      // Optimistic: move the viewer's choice (and, on a revealed poll, their
      // vote and face) immediately; the response carries the real tallies.
      const snapshot = patchPostInCaches(qc, postId, (post) =>
        post.poll
          ? { ...post, poll: applyOptimisticPollVote(post.poll, optionId, viewerId) }
          : post,
      );
      return snapshot;
    },
    onError: (_err, _vars, ctx) => {
      restoreSnapshots(qc, ctx);
    },
    onSuccess: (poll, { postId, section }) => {
      // Patch the authoritative results returned by the server into cache.
      patchPostInCaches(qc, postId, (post) =>
        post.poll ? { ...post, poll } : post,
      );
      trackPollVoted({ post_id: postId, content_section: section });
    },
    onSettled: (_data, _err, { postId }) => {
      qc.invalidateQueries({ queryKey: feedKeys.detail(postId).queryKey });
      // A cast or changed vote moves the viewer between option rosters.
      qc.invalidateQueries({ queryKey: feedKeys.pollVotersAll(postId) });
    },
  });
}

export function useSaveFeedPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      postId: string;
      // Analytics-only: the post's content_section, from the rendering card.
      section: ContentSection;
    }) => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/posts/{id}/save',
        { params: { path: { id: input.postId } } },
      ));
    },
    onMutate: async ({ postId }): Promise<SnapshotSet> => {
      await qc.cancelQueries({ queryKey: feedKeys.lists() });
      await qc.cancelQueries({ queryKey: feedKeys.detail(postId).queryKey });
      const patchSnap = patchPostInCaches(qc, postId, (post) => ({
        ...post,
        viewerSaved: true,
      }));
      const prependSnap = prependToSavedScopeLists(qc, postId);
      return mergeSnapshots(patchSnap, prependSnap);
    },
    onError: (_err, _vars, ctx) => {
      restoreSnapshots(qc, ctx);
    },
    // This hook only ever saves (unsave is useUnsaveFeedPost), so success
    // always means the tracked direction.
    onSuccess: (_data, vars) => {
      trackPostSaved({ content_section: vars.section });
    },
    onSettled: (_data, _err, { postId }) => {
      qc.invalidateQueries({ queryKey: feedKeys.detail(postId).queryKey });
      qc.invalidateQueries({ queryKey: feedKeys.scopeCounts().queryKey });
      // Saved-scope cache MUST refresh now, not on next tab activation —
      // otherwise the tab switch flashes stale rows over the optimistic
      // prepend. See lib/query-cache-utils.ts:invalidateAndRefetchAll.
      invalidateAndRefetchAll(qc, {
        predicate: (q) => isSavedScopeFeedListKey(q.queryKey),
      });
    },
  });
}

export function useUnsaveFeedPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (postId: string) => {
      const client = getApiClient();
      unwrapVoid(await client.DELETE(
        '/api/v1/posts/{id}/save',
        { params: { path: { id: postId } } },
      ));
    },
    onMutate: async (postId): Promise<SnapshotSet> => {
      await qc.cancelQueries({ queryKey: feedKeys.lists() });
      await qc.cancelQueries({ queryKey: feedKeys.detail(postId).queryKey });
      const patchSnap = patchPostInCaches(qc, postId, (post) => ({
        ...post,
        viewerSaved: false,
      }));
      const removeSnap = removeFromSavedScopeLists(qc, postId);
      return mergeSnapshots(patchSnap, removeSnap);
    },
    onError: (_err, _postId, ctx) => {
      restoreSnapshots(qc, ctx);
    },
    onSettled: (_data, _err, postId) => {
      qc.invalidateQueries({ queryKey: feedKeys.detail(postId).queryKey });
      qc.invalidateQueries({ queryKey: feedKeys.scopeCounts().queryKey });
      invalidateAndRefetchAll(qc, {
        predicate: (q) => isSavedScopeFeedListKey(q.queryKey),
      });
    },
  });
}

export function useCreateFeedComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCommentInput) => {
      const client = getApiClient();
      const data = unwrap(await client.POST(
        '/api/v1/posts/{id}/comments',
        {
          params: { path: { id: input.postId } },
          body: {
            body: input.body,
            bodyFormat: input.bodyFormat,
            parentCommentId: input.parentCommentId,
            // Cast back to the codegen's quirky element type (see
            // CreateCommentInput above). Submitted verbatim — the server
            // re-validates before write.
            linkPreviews:
              input.linkPreviews !== undefined
                ? ([...input.linkPreviews] as NonNullable<
                    components['schemas']['CreateCommentRequest']['linkPreviews']
                  >)
                : undefined,
            ...(input.clientCommentId !== undefined
              ? { clientCommentId: input.clientCommentId }
              : {}),
          },
        },
      ));
      return data.data;
    },
    onSuccess: (data, vars) => {
      if (vars.parentCommentId != null) {
        // Replies live in their thread's own cache. Append the created reply
        // to the last loaded page (threads are oldest-first, so new replies
        // belong at the end) instead of invalidating: refetching keyset pages
        // wouldn't pick up a row created past the last page's cursor window.
        qc.setQueryData<InfiniteData<CommentPage>>(
          feedKeys.commentReplies(vars.parentCommentId).queryKey,
          (existing) => {
            if (existing === undefined || existing.pages.length === 0) {
              // Thread never fetched — the viewer replied off the seeded
              // first reply (or a collapsed thread). Materialize a single
              // cursor-less page holding the created reply so it renders
              // without a thread fetch; ReplyThread's expand refetches the
              // real pages (the synthetic page never paginates).
              return {
                pages: [{ items: [data], nextCursor: null, pageSize: 1 }],
                pageParams: [undefined],
              };
            }
            if (
              existing.pages.some((p) => p.items.some((c) => c.id === data.id))
            ) {
              return existing;
            }
            const last = existing.pages.length - 1;
            return {
              ...existing,
              pages: existing.pages.map((p, i) =>
                i === last ? { ...p, items: [...p.items, data] } : p,
              ),
            };
          },
        );
        // Keep the parent's "View replies (N)" count honest until the next
        // top-level refetch confirms it.
        patchCommentInCaches(qc, vars.postId, vars.parentCommentId, (c) => ({
          ...c,
          replyCount: c.replyCount + 1,
        }));
      }
      qc.invalidateQueries({ queryKey: feedKeys.comments(vars.postId).queryKey });
      // bump the cached comment count on the post itself
      patchPostInCaches(qc, vars.postId, (post) => ({
        ...post,
        commentCount: (post.commentCount ?? 0) + 1,
      }));
      trackPostCommented({
        content_section: vars.section,
        has_mention: data.mentions.length > 0,
      });
    },
  });
}

export function useReactToFeedComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      commentId: string;
      // Locates the comment's caches — the reaction endpoint itself is
      // comment-scoped.
      postId: string;
      kind: ReactionKind;
    }) => {
      const client = getApiClient();
      const data = unwrap(await client.POST(
        '/api/v1/posts/comments/{id}/reactions',
        {
          params: { path: { id: input.commentId } },
          body: { kind: input.kind },
        },
      ));
      return data.data;
    },
    onMutate: async ({ postId, commentId, kind }) => {
      await qc.cancelQueries({ queryKey: feedKeys.comments(postId).queryKey });
      await qc.cancelQueries({ queryKey: feedKeys.commentRepliesAll() });
      return patchCommentInCaches(qc, postId, commentId, (comment) => {
        const currentKind = comment.viewerReaction ?? null;
        const removing = currentKind === kind;
        const next = { ...comment.reactionCounts };
        if (currentKind !== null) {
          next[currentKind] = Math.max(0, next[currentKind] - 1);
        }
        if (!removing) next[kind] = next[kind] + 1;
        return {
          ...comment,
          reactionCounts: next,
          viewerReaction: removing ? null : kind,
        };
      });
    },
    onError: (_err, _vars, ctx) => {
      restoreSnapshots(qc, ctx);
    },
    // Settle the caches on the server's authoritative counts — no
    // invalidation needed, the toggle response carries the full state.
    onSuccess: (data, vars) => {
      patchCommentInCaches(qc, vars.postId, vars.commentId, (comment) => ({
        ...comment,
        reactionCounts: data.reactionCounts,
        viewerReaction: data.viewerReaction,
      }));
    },
  });
}

export function useUpdateFeedComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      commentId: string;
      postId: string;
      body: string;
      bodyFormat: 'v1' | 'v2';
    }) => {
      const client = getApiClient();
      const data = unwrap(await client.PATCH(
        '/api/v1/posts/comments/{id}',
        {
          params: { path: { id: input.commentId } },
          body: { body: input.body, bodyFormat: input.bodyFormat },
        },
      ));
      return data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: feedKeys.comments(vars.postId).queryKey });
      // The edited comment may be a reply — refresh any loaded thread caches.
      qc.invalidateQueries({ queryKey: feedKeys.commentRepliesAll() });
    },
  });
}

export function useDeleteFeedComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      commentId: string;
      postId: string;
      /** Rows the server removes: the comment plus its live replies (thread
       *  delete). Callers pass 1 + replyCount for top-level comments. */
      threadSize?: number;
    }) => {
      const client = getApiClient();
      unwrapVoid(await client.DELETE('/api/v1/posts/comments/{id}', {
        params: { path: { id: input.commentId } },
      }));
      return input.commentId;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: feedKeys.comments(vars.postId).queryKey });
      // The deleted comment may be a reply — refresh any loaded thread caches
      // (top-level refetch also restores the parent's replyCount).
      qc.invalidateQueries({ queryKey: feedKeys.commentRepliesAll() });
      // The post's commentCount is server-source-of-truth after delete; drop
      // it locally so the next feed refetch picks up the decremented value.
      patchPostInCaches(qc, vars.postId, (post) => ({
        ...post,
        commentCount: Math.max(
          0,
          (post.commentCount ?? 0) - (vars.threadSize ?? 1),
        ),
      }));
    },
  });
}

export function useDismissFeedPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { postId: string; reason?: string }) => {
      const client = getApiClient();
      unwrapVoid(await client.POST(
        '/api/v1/posts/{id}/dismiss',
        {
          params: { path: { id: input.postId } },
          body: input.reason !== undefined ? { reason: input.reason } : {},
        },
      ));
      return input.postId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feedKeys.lists() });
      qc.invalidateQueries({ queryKey: feedKeys.scopeCounts().queryKey });
    },
  });
}

export function usePresignFeedPostMedia() {
  return useMutation({
    mutationFn: async (input: PresignPostMediaInput): Promise<PresignPostMediaResult> => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/posts/presign-post-media',
        { body: input },
      ));
    },
  });
}

export function useFinalizeFeedPostMedia() {
  return useMutation({
    mutationFn: async (input: FinalizePostMediaInput): Promise<FinalizePostMediaResult> => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/posts/finalize-media',
        { body: input },
      ));
    },
  });
}

export function useResolveFeedUrl() {
  return useMutation({
    mutationFn: async (input: { url: string }): Promise<ResolveUrlResult> => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/posts/resolve-url',
        { body: { url: input.url } },
      ));
    },
  });
}

export function useResolveFeedAttachment() {
  return useMutation({
    mutationFn: async (
      input: AttachedEntityInput,
    ): Promise<ResolveAttachmentResult> => {
      const client = getApiClient();
      return unwrap(await client.POST(
        '/api/v1/posts/attachments/resolve',
        { body: input },
      ));
    },
  });
}

/** Fire-and-forget seen beacon. No cache writes / no invalidation — the seen
 *  signal only affects the NEXT feed load, and a dropped beacon is harmless
 *  (the post just isn't demoted yet). retry:false so a failed beacon never
 *  spins. */
export function useReportImpressions() {
  return useMutation({
    mutationFn: (postIds: string[]) => reportImpressions(postIds),
    retry: false,
  });
}
